import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  getFoodTruck, 
  updateFoodTruck, 
  getCotisationsByFoodTruck, 
  getPaymentsByFoodTruck,
  addDocument,
  deleteDocument,
  getCotisationInvoiceUrl,
  uploadFile,
  payCotisation,
  ouvrirFichier,
} from "../lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Truck,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Euro,
  Users,
  Receipt,
  CreditCard,
  ChefHat,
  Building,
  User,
  Globe,
  FileText,
  Upload,
  Trash2,
  Download,
  Image,
  Bell,
  Utensils,
  CheckSquare,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const DOCUMENT_TYPES = [
  { value: "kbis", label: "Kbis" },
  { value: "identite", label: "Justificatif d'identité" },
  { value: "haccp", label: "Formation HACCP" },
  { value: "assurance", label: "Assurance RC Pro" },
  { value: "carte_grise", label: "Carte grise" },
  { value: "photo", label: "Photo food truck" },
  { value: "menu", label: "Menu / Carte" },
  { value: "logo", label: "Logo" },
  { value: "autre", label: "Autre document" },
];

export default function FoodTruckDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isBureau, user } = useAuth();
  const [foodTruck, setFoodTruck] = useState(null);
  const [cotisations, setCotisations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ type: "", nom: "", url: "", date_expiration: "" });
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [payingCotisationId, setPayingCotisationId] = useState(null);

  // Function to mark cotisation as paid
  const handleMarkCotisationPaid = async (cotisationId) => {
    setPayingCotisationId(cotisationId);
    try {
      await payCotisation(cotisationId);
      toast.success("Cotisation marquée comme payée");
      loadData(); // Reload data to update the UI
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du marquage de la cotisation");
    } finally {
      setPayingCotisationId(null);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      // Les finances ne sont servies qu'au bureau et au propriétaire du food
      // truck : ne pas les demander aux autres, sinon le 403 vide la page.
      const voitFinances = isBureau() || user?.food_truck_id === id;
      const vide = Promise.resolve({ data: [] });
      const [ftRes, cotRes, payRes] = await Promise.all([
        getFoodTruck(id),
        voitFinances ? getCotisationsByFoodTruck(id) : vide,
        voitFinances ? getPaymentsByFoodTruck(id) : vide,
      ]);
      setFoodTruck(ftRes.data);
      setFormData({
        ...ftRes.data,
        societe: ftRes.data.societe || {},
        representant: ftRes.data.representant || {},
        communications: ftRes.data.communications || { plannings: true, festivals: true, demandes: true, partenaires: true },
        point_restauration: ftRes.data.point_restauration || {},
        descriptions: ftRes.data.descriptions || {},
        reseaux_sociaux: ftRes.data.reseaux_sociaux || {},
        cuisine_options: ftRes.data.cuisine_options || {},
        paiements: ftRes.data.paiements || { modes_paiement: [] },
        festival_info: ftRes.data.festival_info || {},
        autres: ftRes.data.autres || {},
      });
      setCotisations(cotRes.data);
      setPayments(payRes.data);
    } catch (error) {
      toast.error("Erreur de chargement");
      navigate("/food-trucks");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateFoodTruck(id, formData);
      toast.success("Modifications enregistrées");
      setEditing(false);
      loadData();
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      const errorDetail = error.response?.data?.detail || error.message || "Erreur inconnue";
      toast.error(`Erreur de mise à jour: ${errorDetail}`);
    }
  };

  const handleAddDocument = async () => {
    if (!docForm.type || !docForm.nom || !docForm.url) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    try {
      await addDocument(id, docForm);
      toast.success("Document ajouté");
      setDocDialog(false);
      setDocForm({ type: "", nom: "", url: "", date_expiration: "" });
      loadData();
    } catch (error) {
      toast.error("Erreur d'ajout");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadFile(file);
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      setDocForm({ 
        ...docForm, 
        url: `${baseUrl}${response.data.url}`,
        nom: docForm.nom || file.name 
      });
      toast.success("Fichier uploadé");
    } catch (error) {
      toast.error("Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocTarget) return;
    try {
      await deleteDocument(id, deleteDocTarget);
      toast.success("Document supprimé");
      setDeleteDocTarget(null);
      loadData();
    } catch (error) {
      toast.error("Erreur de suppression");
    }
  };

  const downloadInvoice = (cotisationId) => {
    const token = localStorage.getItem("token");
    const url = getCotisationInvoiceUrl(cotisationId);
    
    fetch(url, {
      headers: { "Authorization": `Bearer ${token}` }
    })
    .then(res => res.blob())
    .then(blob => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `facture_cotisation.pdf`;
      link.click();
      toast.success("Facture téléchargée");
    })
    .catch(() => toast.error("Erreur de téléchargement"));
  };

  const canEdit = isAdmin() || user?.food_truck_id === id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!foodTruck) return null;

  return (
    <div className="space-y-6" data-testid="food-truck-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/food-trucks")}
          className="text-secondary hover:text-primary"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
            {foodTruck.nom_food_truck}
          </h1>
          <p className="text-muted-foreground flex items-center gap-2 mt-1">
            <ChefHat className="w-4 h-4" /> {foodTruck.specialite}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={foodTruck.statut === "actif" ? "default" : "secondary"} className={foodTruck.statut === "actif" ? "bg-status-success" : ""}>
            {foodTruck.statut}
          </Badge>
          {foodTruck.membre_bureau && (
            <Badge variant="outline" className="border-highlight text-highlight">
              <Users className="w-3 h-3 mr-1" /> Bureau
            </Badge>
          )}
        </div>
      </div>

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-[#F0EBE6] flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="info" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Infos générales
          </TabsTrigger>
          <TabsTrigger value="societe" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Société
          </TabsTrigger>
          <TabsTrigger value="activite" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Activité
          </TabsTrigger>
          <TabsTrigger value="photos" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Photos
          </TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Documents
          </TabsTrigger>
          <TabsTrigger value="cotisations" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Cotisations
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Paiements
          </TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl text-secondary">
                Informations générales
              </CardTitle>
              {canEdit && (
                <div className="flex gap-2">
                  {editing ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => { setEditing(false); loadData(); }}>
                        <X className="w-4 h-4 mr-1" /> Annuler
                      </Button>
                      <Button size="sm" className="bg-primary hover:bg-primary-hover" onClick={handleSave} data-testid="save-food-truck-btn">
                        <Save className="w-4 h-4 mr-1" /> Enregistrer
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-food-truck-btn">
                      <Edit className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <Truck className="w-4 h-4" /> Nom du food truck
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.nom_food_truck}
                        onChange={(e) => setFormData({ ...formData, nom_food_truck: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.nom_food_truck}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <ChefHat className="w-4 h-4" /> Spécialité
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.specialite}
                        onChange={(e) => setFormData({ ...formData, specialite: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.specialite}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Nom du contact</Label>
                    {editing ? (
                      <Input
                        value={formData.nom_contact}
                        onChange={(e) => setFormData({ ...formData, nom_contact: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.nom_contact}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Label>
                    {editing ? (
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.email}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Téléphone
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.telephone}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Adresse
                    </Label>
                    {editing ? (
                      <Input
                        value={formData.adresse || ""}
                        onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.adresse || "Non renseignée"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section Véhicule / Électricité */}
              <div className="border-t border-[#E5E0DC] pt-6">
                <h4 className="text-md font-semibold text-secondary mb-4 flex items-center gap-2">
                  <Truck className="w-4 h-4" /> Véhicule & Équipement
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-secondary font-semibold">Type de véhicule</Label>
                    {editing ? (
                      <Select 
                        value={formData.type_vehicule || ""} 
                        onValueChange={(val) => setFormData({ ...formData, type_vehicule: val })}
                      >
                        <SelectTrigger className="mt-1 border-[#E5E0DC]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Camion">Camion</SelectItem>
                          <SelectItem value="Remorque">Remorque</SelectItem>
                          <SelectItem value="Triporteur">Triporteur</SelectItem>
                          <SelectItem value="Stand">Stand</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.type_vehicule || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Puissance électrique</Label>
                    {editing ? (
                      <Input
                        value={formData.puissance_electrique || ""}
                        onChange={(e) => setFormData({ ...formData, puissance_electrique: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="Ex: 2 PC 16A, 32A triphasé..."
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.puissance_electrique || "Non renseignée"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Plaque d'immatriculation</Label>
                    {editing ? (
                      <Input
                        value={formData.plaque_immatriculation || ""}
                        onChange={(e) => setFormData({ ...formData, plaque_immatriculation: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="Ex: AB-123-CD"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.plaque_immatriculation || "Non renseignée"}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
                  <div>
                    <Label className="text-secondary font-semibold">Longueur (m)</Label>
                    {editing ? (
                      <Input
                        value={formData.longueur || ""}
                        onChange={(e) => setFormData({ ...formData, longueur: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="Ex: 6.5"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.longueur || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Largeur (m)</Label>
                    {editing ? (
                      <Input
                        value={formData.largeur || ""}
                        onChange={(e) => setFormData({ ...formData, largeur: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="Ex: 2.2"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.largeur || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Hauteur (m)</Label>
                    {editing ? (
                      <Input
                        value={formData.hauteur || ""}
                        onChange={(e) => setFormData({ ...formData, hauteur: e.target.value })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="Ex: 3.0"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.hauteur || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Côté ouverture</Label>
                    {editing ? (
                      <Select 
                        value={formData.ouverture || formData.cote_ouverture || ""} 
                        onValueChange={(val) => setFormData({ ...formData, ouverture: val, cote_ouverture: val })}
                      >
                        <SelectTrigger className="mt-1 border-[#E5E0DC]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Gauche">Gauche</SelectItem>
                          <SelectItem value="Droite">Droite</SelectItem>
                          <SelectItem value="Arrière">Arrière</SelectItem>
                          <SelectItem value="Les deux côtés">Les deux côtés</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.ouverture || foodTruck.cote_ouverture || "-"}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E5E0DC] pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Date d'adhésion
                    </Label>
                    <p className="mt-1 text-foreground">
                      {foodTruck.date_adhesion ? format(parseISO(foodTruck.date_adhesion), "dd MMMM yyyy", { locale: fr }) : "Non renseignée"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold flex items-center gap-2">
                      <Euro className="w-4 h-4" /> Cotisation annuelle
                    </Label>
                    {editing ? (
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={formData.cotisation_annuelle === 0 ? "" : formData.cotisation_annuelle}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, cotisation_annuelle: val === "" ? 0 : parseFloat(val) || 0 });
                        }}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="0.00"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.cotisation_annuelle} €</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Statut</Label>
                    {editing && isAdmin() ? (
                      <Select value={formData.statut} onValueChange={(val) => setFormData({ ...formData, statut: val })}>
                        <SelectTrigger className="mt-1 border-[#E5E0DC]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="actif">Actif</SelectItem>
                          <SelectItem value="inactif">Inactif</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-1 text-foreground capitalize">{foodTruck.statut}</p>
                    )}
                  </div>
                </div>
              </div>

              {editing && isAdmin() && (
                <div className="border-t border-[#E5E0DC] pt-6">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.membre_bureau}
                      onCheckedChange={(checked) => setFormData({ ...formData, membre_bureau: checked })}
                    />
                    <Label className="text-secondary font-semibold">Membre du bureau</Label>
                  </div>
                </div>
              )}

              <div className="border-t border-[#E5E0DC] pt-6">
                <div className="p-4 rounded-lg bg-[#F9F7F5] flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Solde dû</p>
                    <p className={`text-2xl font-heading font-bold ${foodTruck.solde_du > 0 ? "text-status-warning" : "text-status-success"}`}>
                      {foodTruck.solde_du.toFixed(2)} €
                    </p>
                  </div>
                  {foodTruck.solde_du === 0 && (
                    <Badge className="bg-status-success text-white">À jour</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Société Tab */}
        <TabsContent value="societe">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Building className="w-5 h-5 text-primary" />
                  Identité de la société
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">SIREN</Label>
                    {editing ? (
                      <Input
                        value={formData.societe?.siren || ""}
                        onChange={(e) => setFormData({ ...formData, societe: { ...formData.societe, siren: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="123456789"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.societe?.siren || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">SIRET</Label>
                    {editing ? (
                      <Input
                        value={formData.societe?.siret || ""}
                        onChange={(e) => setFormData({ ...formData, societe: { ...formData.societe, siret: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="12345678900012"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.societe?.siret || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Code APE</Label>
                    {editing ? (
                      <Input
                        value={formData.societe?.code_ape || ""}
                        onChange={(e) => setFormData({ ...formData, societe: { ...formData.societe, code_ape: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="5610C"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.societe?.code_ape || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">N° TVA</Label>
                    {editing ? (
                      <Input
                        value={formData.societe?.numero_tva || ""}
                        onChange={(e) => setFormData({ ...formData, societe: { ...formData.societe, numero_tva: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="FR12345678901"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.societe?.numero_tva || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Forme juridique</Label>
                    {editing ? (
                      <Select 
                        value={formData.societe?.forme_juridique || ""} 
                        onValueChange={(val) => setFormData({ ...formData, societe: { ...formData.societe, forme_juridique: val } })}
                      >
                        <SelectTrigger className="mt-1 border-[#E5E0DC]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EURL">EURL</SelectItem>
                          <SelectItem value="SARL">SARL</SelectItem>
                          <SelectItem value="SAS">SAS</SelectItem>
                          <SelectItem value="SASU">SASU</SelectItem>
                          <SelectItem value="Auto-entrepreneur">Auto-entrepreneur</SelectItem>
                          <SelectItem value="EI">Entreprise Individuelle</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.societe?.forme_juridique || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Nb salariés</Label>
                    {editing ? (
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={formData.societe?.nbre_salaries || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, societe: { ...formData.societe, nbre_salaries: val === "" ? 0 : parseInt(val) || 0 } });
                        }}
                        className="mt-1 border-[#E5E0DC]"
                        placeholder="0"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.societe?.nbre_salaries ?? "Non renseigné"}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Représentant principal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Nom complet</Label>
                  {editing ? (
                    <Input
                      value={formData.representant?.nom || ""}
                      onChange={(e) => setFormData({ ...formData, representant: { ...formData.representant, nom: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.representant?.nom || "Non renseigné"}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Email</Label>
                    {editing ? (
                      <Input
                        type="email"
                        value={formData.representant?.email || ""}
                        onChange={(e) => setFormData({ ...formData, representant: { ...formData.representant, email: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.representant?.email || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Téléphone</Label>
                    {editing ? (
                      <Input
                        value={formData.representant?.telephone || ""}
                        onChange={(e) => setFormData({ ...formData, representant: { ...formData.representant, telephone: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.representant?.telephone || "Non renseigné"}</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Portable</Label>
                  {editing ? (
                    <Input
                      value={formData.representant?.portable || ""}
                      onChange={(e) => setFormData({ ...formData, representant: { ...formData.representant, portable: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.representant?.portable || "Non renseigné"}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  Préférences de communication
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {["plannings", "festivals", "demandes", "partenaires"].map((pref) => (
                  <div key={pref} className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg">
                    <span className="capitalize">{pref}</span>
                    {editing ? (
                      <Switch
                        checked={formData.communications?.[pref] ?? true}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          communications: { ...formData.communications, [pref]: checked } 
                        })}
                      />
                    ) : (
                      <Badge variant={foodTruck.communications?.[pref] ? "default" : "secondary"} className={foodTruck.communications?.[pref] ? "bg-status-success" : ""}>
                        {foodTruck.communications?.[pref] ? "Oui" : "Non"}
                      </Badge>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Réseaux sociaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Site web</Label>
                  {editing ? (
                    <Input
                      value={formData.reseaux_sociaux?.site_web || ""}
                      onChange={(e) => setFormData({ ...formData, reseaux_sociaux: { ...formData.reseaux_sociaux, site_web: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="https://"
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.reseaux_sociaux?.site_web || "Non renseigné"}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-secondary font-semibold">Facebook</Label>
                    {editing ? (
                      <Input
                        value={formData.reseaux_sociaux?.facebook || ""}
                        onChange={(e) => setFormData({ ...formData, reseaux_sociaux: { ...formData.reseaux_sociaux, facebook: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.reseaux_sociaux?.facebook || "Non renseigné"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Instagram</Label>
                    {editing ? (
                      <Input
                        value={formData.reseaux_sociaux?.instagram || ""}
                        onChange={(e) => setFormData({ ...formData, reseaux_sociaux: { ...formData.reseaux_sociaux, instagram: e.target.value } })}
                        className="mt-1 border-[#E5E0DC]"
                      />
                    ) : (
                      <p className="mt-1 text-foreground">{foodTruck.reseaux_sociaux?.instagram || "Non renseigné"}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Activité Tab */}
        <TabsContent value="activite">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-primary" />
                  Point de restauration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Nom commercial</Label>
                  {editing ? (
                    <Input
                      value={formData.point_restauration?.nom_commercial || ""}
                      onChange={(e) => setFormData({ ...formData, point_restauration: { ...formData.point_restauration, nom_commercial: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.point_restauration?.nom_commercial || "Non renseigné"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Date début activité</Label>
                  {editing ? (
                    <Input
                      type="date"
                      value={formData.point_restauration?.date_debut_activite || ""}
                      onChange={(e) => setFormData({ ...formData, point_restauration: { ...formData.point_restauration, date_debut_activite: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.point_restauration?.date_debut_activite || "Non renseigné"}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Descriptions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-secondary font-semibold">Résumé</Label>
                  {editing ? (
                    <Textarea
                      value={formData.descriptions?.resume || ""}
                      onChange={(e) => setFormData({ ...formData, descriptions: { ...formData.descriptions, resume: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.descriptions?.resume || "Non renseigné"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-secondary font-semibold">Concept</Label>
                  {editing ? (
                    <Textarea
                      value={formData.descriptions?.concept || ""}
                      onChange={(e) => setFormData({ ...formData, descriptions: { ...formData.descriptions, concept: e.target.value } })}
                      className="mt-1 border-[#E5E0DC]"
                      rows={3}
                    />
                  ) : (
                    <p className="mt-1 text-foreground">{foodTruck.descriptions?.concept || "Non renseigné"}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E0DC]">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-primary" />
                  Options & Services
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: "service_stand", label: "Service en stand" },
                  { key: "electrique_100", label: "100% électrique" },
                ].map((opt) => (
                  <div key={opt.key} className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg">
                    <span>{opt.label}</span>
                    {editing ? (
                      <Switch
                        checked={formData.cuisine_options?.[opt.key] ?? false}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          cuisine_options: { ...formData.cuisine_options, [opt.key]: checked } 
                        })}
                      />
                    ) : (
                      <Badge variant={foodTruck.cuisine_options?.[opt.key] ? "default" : "secondary"} className={foodTruck.cuisine_options?.[opt.key] ? "bg-status-success" : ""}>
                        {foodTruck.cuisine_options?.[opt.key] ? "Oui" : "Non"}
                      </Badge>
                    )}
                  </div>
                ))}
                {[
                  { key: "vegan", label: "Vegan" },
                  { key: "vegetarien", label: "Végétarien" },
                  { key: "halal", label: "Halal" },
                ].map((opt) => (
                  <div key={opt.key}>
                    <Label className="text-secondary font-semibold">{opt.label}</Label>
                    {editing ? (
                      <Select 
                        value={formData.cuisine_options?.[opt.key] || "non"} 
                        onValueChange={(val) => setFormData({ ...formData, cuisine_options: { ...formData.cuisine_options, [opt.key]: val } })}
                      >
                        <SelectTrigger className="mt-1 border-[#E5E0DC]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="non">Non</SelectItem>
                          <SelectItem value="option">En option</SelectItem>
                          <SelectItem value="oui">Oui</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-1 text-foreground capitalize">{foodTruck.cuisine_options?.[opt.key] || "Non"}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Photos du Food Truck
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Camion Extérieur */}
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-3 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-primary" />
                  Camion Extérieur
                </h3>
                {foodTruck.photos?.camion_exterieur?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {foodTruck.photos.camion_exterieur.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E0DC]">
                        <img
                          src={`${API_URL}/api/files/${photo.file_id}`}
                          alt={`Camion extérieur ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune photo du camion extérieur</p>
                )}
              </div>

              {/* Camion Intérieur */}
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-3 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-accent" />
                  Camion Intérieur
                </h3>
                {foodTruck.photos?.camion_interieur?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {foodTruck.photos.camion_interieur.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E0DC]">
                        <img
                          src={`${API_URL}/api/files/${photo.file_id}`}
                          alt={`Camion intérieur ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune photo du camion intérieur</p>
                )}
              </div>

              {/* Menu */}
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Menu
                </h3>
                {foodTruck.photos?.menu?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {foodTruck.photos.menu.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E0DC]">
                        <img
                          src={`${API_URL}/api/files/${photo.file_id}`}
                          alt={`Menu ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune photo du menu</p>
                )}
              </div>

              {/* Plats */}
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-3 flex items-center gap-2">
                  <ChefHat className="w-5 h-5 text-accent" />
                  Plats
                </h3>
                {foodTruck.photos?.plats?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {foodTruck.photos.plats.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E0DC]">
                        <img
                          src={`${API_URL}/api/files/${photo.file_id}`}
                          alt={`Plat ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune photo de plats</p>
                )}
              </div>

              {/* No photos at all */}
              {(!foodTruck.photos || 
                ((!foodTruck.photos.camion_exterieur || foodTruck.photos.camion_exterieur.length === 0) &&
                 (!foodTruck.photos.camion_interieur || foodTruck.photos.camion_interieur.length === 0) &&
                 (!foodTruck.photos.menu || foodTruck.photos.menu.length === 0) &&
                 (!foodTruck.photos.plats || foodTruck.photos.plats.length === 0))) && (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune photo n'a été uploadée par ce food truck</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Documents
              </CardTitle>
              {canEdit && (
                <Button 
                  size="sm" 
                  className="bg-primary hover:bg-primary-hover"
                  onClick={() => setDocDialog(true)}
                  data-testid="add-document-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {foodTruck.documents && foodTruck.documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {foodTruck.documents.map((doc) => (
                    <div key={doc.id} className="p-4 bg-[#F9F7F5] rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {doc.type === "photo" || doc.type === "logo" ? (
                            <Image className="w-5 h-5 text-accent" />
                          ) : (
                            <FileText className="w-5 h-5 text-primary" />
                          )}
                          <Badge variant="outline">{DOCUMENT_TYPES.find(t => t.value === doc.type)?.label || doc.type}</Badge>
                        </div>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDocTarget(doc.id)}
                            className="text-status-error hover:text-status-error/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="font-medium text-secondary truncate">{doc.nom}</p>
                      {doc.date_expiration && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Expire le: {doc.date_expiration}
                        </p>
                      )}
                      <a 
                        href={doc.url} 
                        onClick={(e) => { e.preventDefault(); ouvrirFichier(doc.url).catch(() => toast.error("Document indisponible ou accès refusé")); }}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <Download className="w-4 h-4" /> Télécharger
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun document</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ajoutez vos documents : Kbis, HACCP, Assurance, Photos...
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cotisations Tab */}
        <TabsContent value="cotisations">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Receipt className="w-5 h-5 text-primary" />
                Historique des cotisations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cotisations.length > 0 ? (
                <div className="space-y-3">
                  {cotisations.map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-4 bg-[#F9F7F5] rounded-lg">
                      <div>
                        <p className="font-semibold text-secondary">Cotisation {c.annee}</p>
                        <p className="text-sm text-muted-foreground">
                          {c.date_paiement ? `Payée le ${format(parseISO(c.date_paiement), "dd/MM/yyyy")}` : "En attente"}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-secondary">{c.montant} €</span>
                        <Badge variant={c.statut === "paye" ? "default" : "secondary"} className={c.statut === "paye" ? "bg-status-success" : "bg-status-warning text-white"}>
                          {c.statut === "paye" ? "Payée" : "Non payée"}
                        </Badge>
                        {c.statut === "paye" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoice(c.id)}
                            className="text-primary hover:text-primary-hover"
                            data-testid={`download-invoice-${c.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> Facture
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleMarkCotisationPaid(c.id)}
                            disabled={payingCotisationId === c.id}
                            className="bg-status-success hover:bg-status-success/90 text-white"
                            data-testid={`mark-paid-${c.id}`}
                          >
                            {payingCotisationId === c.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Marquer payée
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucune cotisation</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Historique des paiements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payments.length > 0 ? (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-[#F9F7F5] rounded-lg">
                      <div>
                        <p className="font-semibold text-secondary capitalize">{p.type.replace("_", " ")}</p>
                        <p className="text-sm text-muted-foreground">
                          {p.date_paiement ? format(parseISO(p.date_paiement), "dd MMMM yyyy", { locale: fr }) : "Date non renseignée"}
                        </p>
                      </div>
                      <span className="font-bold text-status-success">+{p.montant} €</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Aucun paiement enregistré</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Document Dialog */}
      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-primary">
              Ajouter un document
            </DialogTitle>
            <DialogDescription>
              Renseignez les informations du document
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-secondary font-semibold">Type de document *</Label>
              <Select value={docForm.type} onValueChange={(val) => setDocForm({ ...docForm, type: val })}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondary font-semibold">Nom du document *</Label>
              <Input
                value={docForm.nom}
                onChange={(e) => setDocForm({ ...docForm, nom: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Ex: Kbis 2024"
              />
            </div>
            <div>
              <Label className="text-secondary font-semibold">URL du document *</Label>
              <Input
                value={docForm.url}
                onChange={(e) => setDocForm({ ...docForm, url: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
                placeholder="https://..."
              />
            </div>
            <div>
              <Label className="text-secondary font-semibold">Date d'expiration</Label>
              <Input
                type="date"
                value={docForm.date_expiration}
                onChange={(e) => setDocForm({ ...docForm, date_expiration: e.target.value })}
                className="mt-1 border-[#E5E0DC]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDialog(false)}>Annuler</Button>
            <Button className="bg-primary hover:bg-primary-hover" onClick={handleAddDocument}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!deleteDocTarget} onOpenChange={() => setDeleteDocTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              className="bg-status-error hover:bg-status-error/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
